import type { ApiOrganization } from '../queries/register-organization/index';
import {
  ASSOCIATIONS_CODES,
  BE_VZW_LEGAL_CODE,
  CIVIL_COMPANY_CODES,
  EGBR_LEGAL_CODE,
  ENI_LEGAL_CODE,
  GBR_LEGAL_CODE,
  GBR_MANAGEMENT_TYPES,
  ITALIAN_COOPERATION_CREDITSAFE_CODES,
  ITALIAN_FOUNDATION_CREDITSAFE_CODES,
  ITALIAN_LIMITED_COMPANY_CREDITSAFE_CODES,
  ITALIAN_PARTNERSHIP_CREDITSAFE_CODES,
  NETHERLANDS_AND_BELGIUM_UBO_LEGAL_FORM_CODES,
  NL_STICHTING_LEGAL_CODE,
  NON_POWERABLE_CREDITSAFE_CODES,
  ORGANIZATION_TYPES,
  PARTG_LEGAL_CODE,
  PARTGMBB_LEGAL_CODE,
  PHYSICAL_PERSON_COMPANY_TYPES,
  TRABALHADOR_INDEPENDENTE,
  UNIPERSONAL_CODES,
  VEREINE_LEGAL_CODE,
  ZELFSTANDIGE_LEGAL_CODE,
} from './constants';

export const UPMARKET_EMPLOYEE_RANGES = ['6-9', '10+'];

export function isUpmarketRegister(organization: ApiOrganization): boolean | undefined {
  const range = organization.declaredNumberOfEmployeeRange;
  if (!range) return undefined;

  return UPMARKET_EMPLOYEE_RANGES.includes(range);
}

function includesString<T extends string>(
  array: ReadonlyArray<T>,
  value: string | null | undefined
): value is T {
  // oxlint-disable-next-line typescript/consistent-type-assertions
  return typeof value === 'string' && (array as readonly string[]).includes(value);
}

export function computeIsAssociation(organization: ApiOrganization | null): boolean {
  if (!organization) {
    return false;
  }

  if (organization.legalFormCreditsafeCode) {
    return isAssociation(organization);
  }

  return isAssociationInitialOrganizationType(organization);
}

export function isFreelancerInitialOrganizationType({
  initialOrganizationType,
}: {
  initialOrganizationType?: string | null;
}): boolean {
  return initialOrganizationType === ORGANIZATION_TYPES.FREELANCER;
}

export function isFreelancer({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return includesString(UNIPERSONAL_CODES, legalFormCreditsafeCode);
}

export function isAssociationInitialOrganizationType({
  initialOrganizationType,
}: {
  initialOrganizationType?: string | null;
}): boolean {
  return initialOrganizationType === ORGANIZATION_TYPES.ASSOCIATION;
}

export function isAssociation({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return includesString(ASSOCIATIONS_CODES, legalFormCreditsafeCode);
}

export function isItalianFoundation({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return includesString(ITALIAN_FOUNDATION_CREDITSAFE_CODES, legalFormCreditsafeCode);
}

export function isItalianAssociationOrFoundation({
  legalCountry,
  legalFormCreditsafeCode,
}: {
  legalCountry?: string | null;
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return (
    legalCountry === 'IT' &&
    (isAssociation({ legalFormCreditsafeCode }) || isItalianFoundation({ legalFormCreditsafeCode }))
  );
}

export function isItalian({ legalCountry }: { legalCountry?: string | null }): boolean {
  return legalCountry === 'IT';
}

export function isSpanish({ legalCountry }: { legalCountry?: string | null }): boolean {
  return legalCountry === 'ES';
}

export function isFrench({ legalCountry }: { legalCountry?: string | null }): boolean {
  return legalCountry === 'FR';
}

const ES_NEW_ACTIVITY_SCREEN_FEATURE = 'es_new_activity_screen_be';

/**
 * True when the backend has enabled the dedicated ES activity screen for this
 * organization. The BE then collects activity data on `/activity`, so the ES
 * company-info form must hide (and not submit) those fields.
 */
export function hasEsNewActivityScreen({ features }: { features?: readonly string[] }): boolean {
  return features?.includes(ES_NEW_ACTIVITY_SCREEN_FEATURE) ?? false;
}

export const EARLY_PHONE_6_9_FEATURE = 'early_phone_6_9';

export function earlyPhone69Variant(
  organization: ApiOrganization
): 'treatment' | 'control' | undefined {
  if (organization.declaredNumberOfEmployeeRange !== '6-9') {
    return undefined;
  }

  return organization.features.includes(EARLY_PHONE_6_9_FEATURE) ? 'treatment' : 'control';
}

export function isSpanishCapitalDepositFlow({
  legalCountry,
  underRegistration,
}: {
  legalCountry?: string | null;
  underRegistration?: boolean | null;
}): boolean {
  return legalCountry === 'ES' && Boolean(underRegistration);
}

export function isWorksCouncilInitialOrganizationType({
  initialOrganizationType,
}: {
  initialOrganizationType?: string | null;
}): boolean {
  return initialOrganizationType === ORGANIZATION_TYPES.WORKS_COUNCIL;
}

export function isPowerable({
  legalFormCreditsafeCode,
  initialOrganizationType,
}: {
  legalFormCreditsafeCode?: string | null;
  initialOrganizationType?: string | null;
}): boolean {
  if (!legalFormCreditsafeCode) {
    return false;
  }
  return (
    !includesString(NON_POWERABLE_CREDITSAFE_CODES, legalFormCreditsafeCode) &&
    (!isAssociationInitialOrganizationType({ initialOrganizationType }) ||
      !includesString(ASSOCIATIONS_CODES, legalFormCreditsafeCode))
  );
}

export function isPortugueseFreelancer({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return (
    legalFormCreditsafeCode === TRABALHADOR_INDEPENDENTE ||
    legalFormCreditsafeCode === ENI_LEGAL_CODE
  );
}

export function isBelgianFreelancer({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === ZELFSTANDIGE_LEGAL_CODE;
}

export function isPassiveNonFinancialEntity({
  businessActivityEntityType,
}: {
  businessActivityEntityType?: string | null;
}): boolean {
  return businessActivityEntityType === 'passive_nfe';
}

export function isPhysicalPersonEntity({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return includesString(PHYSICAL_PERSON_COMPANY_TYPES, legalFormCreditsafeCode);
}

export function isWorksCouncil({
  initialOrganizationType,
}: {
  initialOrganizationType?: string | null;
}): boolean {
  return initialOrganizationType === ORGANIZATION_TYPES.WORKS_COUNCIL;
}

export function isGbr({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === GBR_LEGAL_CODE;
}

export function isEgbr({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === EGBR_LEGAL_CODE;
}

const GBR_LIKE_CODES: string[] = [GBR_LEGAL_CODE, EGBR_LEGAL_CODE];

export function isGbrLike({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  if (!legalFormCreditsafeCode) {
    return false;
  }
  return GBR_LIKE_CODES.includes(legalFormCreditsafeCode);
}

export function isGbrSingleManagement({
  gbrManagementType,
}: {
  gbrManagementType?: string | null;
}): boolean {
  return gbrManagementType === GBR_MANAGEMENT_TYPES.SINGLE;
}

export function hasPaidDeposit({
  depositPaymentId,
}: {
  depositPaymentId?: string | null;
}): boolean {
  return Boolean(depositPaymentId);
}

export function isGermanVereine({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === VEREINE_LEGAL_CODE;
}

export function isNlStichting({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === NL_STICHTING_LEGAL_CODE;
}

export function isItalianSelfEmployed({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === 'IT001' || legalFormCreditsafeCode === 'IT002';
}

export function isItalianFreelancer({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === 'IT002';
}

export function isBelgianNonProfit({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === BE_VZW_LEGAL_CODE;
}

export function isCivilCompany({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return includesString(CIVIL_COMPANY_CODES, legalFormCreditsafeCode);
}

export function isFrenchCompanyCreation({
  legalCountry,
  underRegistration,
}: {
  legalCountry?: string | null;
  underRegistration?: boolean | null;
}): boolean {
  return legalCountry === 'FR' && Boolean(underRegistration);
}

export function isFrenchCompanyCreationFromScratch({
  legalCountry,
  underRegistration,
  companyCreationFromScratch,
}: {
  legalCountry?: string | null;
  underRegistration?: boolean | null;
  companyCreationFromScratch?: boolean | null;
}): boolean {
  return (
    isFrenchCompanyCreation({ legalCountry, underRegistration }) &&
    Boolean(companyCreationFromScratch)
  );
}

export function isItalianLimitedCompany({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return includesString(ITALIAN_LIMITED_COMPANY_CREDITSAFE_CODES, legalFormCreditsafeCode);
}

export function isItalianPartnership({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return includesString(ITALIAN_PARTNERSHIP_CREDITSAFE_CODES, legalFormCreditsafeCode);
}

export function isItalianCooperation({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return includesString(ITALIAN_COOPERATION_CREDITSAFE_CODES, legalFormCreditsafeCode);
}

export function isPartG({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === PARTG_LEGAL_CODE;
}

export function isPartGMbb({
  legalFormCreditsafeCode,
}: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return legalFormCreditsafeCode === PARTGMBB_LEGAL_CODE;
}

export function shouldDisplayPartners(organization: {
  legalFormCreditsafeCode?: string | null;
}): boolean {
  return (
    isGbr(organization) || isEgbr(organization) || isPartG(organization) || isPartGMbb(organization)
  );
}

export function hasSelectBeneficialOwners(organization: ApiOrganization): boolean {
  const { legalCountry, legalFormCreditsafeCode } = organization;

  if (legalCountry === 'IT') {
    return (
      isItalianLimitedCompany({ legalFormCreditsafeCode }) ||
      isItalianPartnership({ legalFormCreditsafeCode }) ||
      isItalianCooperation({ legalFormCreditsafeCode }) ||
      isItalianFoundation({ legalFormCreditsafeCode }) ||
      isAssociation({ legalFormCreditsafeCode })
    );
  }

  if (legalCountry === 'DE') {
    return (
      organization.features.includes('ubo_optimization_de') &&
      ['DE206', 'DE207', 'DE102', 'DE107', 'DE201', 'DE103', 'DE208', 'DE602'].includes(
        legalFormCreditsafeCode ?? ''
      )
    );
  }

  if (legalCountry === 'NL' || legalCountry === 'BE') {
    return includesString(NETHERLANDS_AND_BELGIUM_UBO_LEGAL_FORM_CODES, legalFormCreditsafeCode);
  }

  if (legalCountry === 'FR') {
    return isAssociation({ legalFormCreditsafeCode }) || isWorksCouncil(organization);
  }

  return false;
}
